import React, { Component } from "react";
import { BASE_URL } from "../common/common";

const API_URL = BASE_URL;
const MAX_IMAGE_SIZE = 3 * 1024 * 1024; // 3 MB

const imgUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http")) return `${path}?ngrok-skip-browser-warning=true`;
  const cleanPath = path.startsWith("/") ? path.slice(1) : path;
  return `${API_URL}/${cleanPath}?ngrok-skip-browser-warning=true`;
};

// ── Rich Text Editor ────────────────────────
class RichTextEditor extends Component {
  constructor(props) {
    super(props);
    this.ref = React.createRef();
    this.state = { showLinkInput: false, linkUrl: "" };
  }

  componentDidMount() {
    if (this.ref.current) this.ref.current.innerHTML = this.props.value || "";
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value && this.ref.current) {
      const cursorPos = document.activeElement === this.ref.current;
      if (!cursorPos) this.ref.current.innerHTML = this.props.value || "";
    }
  }

  exec = (cmd, value = null) => {
    this.ref.current.focus();
    document.execCommand(cmd, false, value);
    this.notify();
  };

  notify = () => { if (this.ref.current) this.props.onChange(this.ref.current.innerHTML); };
  handleInput = () => this.notify();

  insertLink = () => {
    const { linkUrl } = this.state;
    if (!linkUrl.trim()) return;
    const url = linkUrl.startsWith("http") ? linkUrl : `https://${linkUrl}`;
    this.exec("createLink", url);
    this.setState({ showLinkInput: false, linkUrl: "" });
  };

  render() {
    const { showLinkInput, linkUrl } = this.state;
    return (
      <div style={rt.wrapper}>
        <div style={rt.toolbar}>
          <select style={rt.select} onChange={(e) => { this.exec("formatBlock", e.target.value); e.target.value = "p"; }}>
            <option value="p">Paragraph</option>
            <option value="h1">H1</option>
            <option value="h2">H2</option>
            <option value="h3">H3</option>
            <option value="h4">H4</option>
            <option value="pre">Code</option>
            <option value="blockquote">Quote</option>
          </select>
          <select style={rt.select} onChange={(e) => { this.exec("fontSize", e.target.value); e.target.value = ""; }}>
            <option value="">Size</option>
            {[1,2,3,4,5,6,7].map((n) => (<option key={n} value={n}>{["8px","10px","12px","14px","18px","24px","36px"][n-1]}</option>))}
          </select>
          <span style={rt.sep} />
          <button type="button" style={rt.btn} onClick={() => this.exec("bold")}><b>B</b></button>
          <button type="button" style={rt.btn} onClick={() => this.exec("italic")}><i>I</i></button>
          <button type="button" style={rt.btn} onClick={() => this.exec("underline")}><u>U</u></button>
          <button type="button" style={rt.btn} onClick={() => this.exec("strikeThrough")}><s>S</s></button>
          <span style={rt.sep} />
          <label style={rt.colorLabel} title="Text Color">A<input type="color" style={rt.colorInput} onChange={(e) => this.exec("foreColor", e.target.value)} /></label>
          <label style={{ ...rt.colorLabel, background: "#ffe082" }} title="Highlight">H<input type="color" style={rt.colorInput} onChange={(e) => this.exec("hiliteColor", e.target.value)} /></label>
          <span style={rt.sep} />
          <button type="button" style={rt.btn} onClick={() => this.exec("justifyLeft")}>&#8676;</button>
          <button type="button" style={rt.btn} onClick={() => this.exec("justifyCenter")}>&#8652;</button>
          <button type="button" style={rt.btn} onClick={() => this.exec("justifyRight")}>&#8677;</button>
          <button type="button" style={rt.btn} onClick={() => this.exec("justifyFull")}>&#8801;</button>
          <span style={rt.sep} />
          <button type="button" style={rt.btn} onClick={() => this.exec("insertUnorderedList")}>• List</button>
          <button type="button" style={rt.btn} onClick={() => this.exec("insertOrderedList")}>1. List</button>
          <button type="button" style={rt.btn} onClick={() => this.exec("indent")}>&#8677;</button>
          <button type="button" style={rt.btn} onClick={() => this.exec("outdent")}>&#8676;</button>
          <span style={rt.sep} />
          <button type="button" style={rt.btn} onClick={() => this.setState((p) => ({ showLinkInput: !p.showLinkInput }))}>Link</button>
          <button type="button" style={rt.btn} onClick={() => this.exec("unlink")}>Unlink</button>
          <span style={rt.sep} />
          <button type="button" style={rt.btn} onClick={() => this.exec("insertHorizontalRule")}>&#8213;</button>
          <button type="button" style={rt.btn} onClick={() => this.exec("removeFormat")}>Clear</button>
          <button type="button" style={rt.btn} onClick={() => this.exec("undo")}>&#8617;</button>
          <button type="button" style={rt.btn} onClick={() => this.exec("redo")}>&#8618;</button>
        </div>
        {showLinkInput && (
          <div style={rt.linkRow}>
            <input type="text" placeholder="https://example.com" value={linkUrl} onChange={(e) => this.setState({ linkUrl: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") this.insertLink(); }} style={rt.linkInput} />
            <button type="button" style={rt.linkBtn} onClick={this.insertLink}>Insert</button>
            <button type="button" style={rt.cancelBtn} onClick={() => this.setState({ showLinkInput: false, linkUrl: "" })}>Cancel</button>
          </div>
        )}
        <div ref={this.ref} contentEditable suppressContentEditableWarning onInput={this.handleInput} style={rt.editor} />
        <div style={rt.footer}>
          {this.ref.current ? this.ref.current.innerText.length : 0} characters
          <span style={{ marginLeft: "12px", color: "#bbb" }}>Select text first, then apply formatting</span>
        </div>
      </div>
    );
  }
}

const rt = {
  wrapper   : { border: "1px solid #ddd", borderRadius: "8px", overflow: "hidden" },
  toolbar   : { display: "flex", flexWrap: "wrap", gap: "3px", padding: "8px 10px", background: "#f8f8f8", borderBottom: "1px solid #e8e8e8", alignItems: "center" },
  btn       : { padding: "4px 9px", background: "white", border: "1px solid #ddd", borderRadius: "4px", cursor: "pointer", fontSize: "12px", minWidth: "28px", textAlign: "center" },
  select    : { padding: "4px 6px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "12px", background: "white", cursor: "pointer", outline: "none" },
  sep       : { width: "1px", height: "20px", background: "#ddd", margin: "0 3px", flexShrink: 0 },
  colorLabel: { padding: "3px 8px", background: "#fff", border: "1px solid #ddd", borderRadius: "4px", cursor: "pointer", fontSize: "12px", fontWeight: "700", position: "relative", display: "flex", alignItems: "center" },
  colorInput: { position: "absolute", opacity: 0, width: "100%", height: "100%", top: 0, left: 0, cursor: "pointer", border: "none" },
  linkRow   : { display: "flex", gap: "8px", padding: "8px 10px", background: "#f0f7ff", borderBottom: "1px solid #bbdefb", alignItems: "center" },
  linkInput : { flex: 1, padding: "6px 10px", border: "1px solid #bbdefb", borderRadius: "6px", fontSize: "13px", outline: "none" },
  linkBtn   : { padding: "6px 14px", background: "#1565c0", color: "#fff", border: "none", borderRadius: "6px", fontSize: "12px", cursor: "pointer", fontWeight: "600" },
  cancelBtn : { padding: "6px 14px", background: "#f0f2f5", color: "#555", border: "1px solid #ddd", borderRadius: "6px", fontSize: "12px", cursor: "pointer" },
  editor    : { minHeight: "160px", padding: "14px", fontSize: "14px", lineHeight: "1.8", outline: "none", fontFamily: "Arial, sans-serif", background: "white" },
  footer    : { padding: "6px 12px", background: "#fafafa", borderTop: "1px solid #f0f0f0", fontSize: "11px", color: "#aaa", display: "flex", alignItems: "center" },
};

// ── helper: strip HTML + &nbsp; → plain text ─
const stripHtml = (str) => {
  if (!str) return "";
  if (Array.isArray(str)) return str.join("  •  ");
  return str
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim();
};

// ── Empty form factory ────────────────────
const emptyServiceForm = () => ({
  name          : "",
  description   : "",
  features      : "",
  event         : "",   // ← NEW
  url           : "",
  images        : [],
  bannerPreview : null,
  bannerFile    : null,
  saving        : false,
});

// ── Main Component ──────────────────────────
class Blog extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTab : "blog",

      // Banner
      bannerView         : "list",
      bannerList         : [],
      bannerLoading      : false,
      bannerTitle        : "Services",
      bannerImagePreview : null,
      bannerFile         : null,
      bannerSaving       : false,
      editingBanner      : null,

      // Services
      serviceView    : "list",
      serviceList    : [],
      serviceLoading : false,
      serviceForm    : emptyServiceForm(),
      editingService : null,

      successMessage : "",
      errorMessage   : "",
    };
  }

  componentDidMount() {
    // this.fetchBanners();
    this.fetchServices();
  }

  showSuccess = (msg) => { this.setState({ successMessage: msg, errorMessage: "" }); setTimeout(() => this.setState({ successMessage: "" }), 3000); };
  showError   = (msg) => { this.setState({ errorMessage: msg, successMessage: "" }); setTimeout(() => this.setState({ errorMessage: "" }), 4000); };

  // ════════════════════
  //  BANNER
  // ════════════════════

  fetchBanners = async () => {
    this.setState({ bannerLoading: true });
    try {
      const res  = await fetch(`${API_URL}/api/banners/getBanners`, { method: "GET", headers: { "ngrok-skip-browser-warning": "true" } });
      const json = await res.json();
      if (json.status) {
        const filtered = (json.data|| []).filter((b) => b.Page_Name?.toLowerCase() === "services");
        this.setState({ bannerList: filtered, bannerLoading: false });
      } else { this.setState({ bannerLoading: false }); }
    } catch { this.setState({ bannerLoading: false }); this.showError("Cannot connect to API."); }
  };

  openBannerForm = (banner = null) => {
    this.setState({ bannerView: "form", editingBanner: banner, bannerTitle: banner?.Page_Name || "Services", bannerImagePreview: null, bannerFile: null });
  };

  goToBannerList = () => { this.setState({ bannerView: "list", editingBanner: null }); this.fetchBanners(); };

  handleBannerImg = (e) => {
    const file = e.target.files[0]; if (!file) return;
    if (file.size > MAX_IMAGE_SIZE) { this.showError("Image size must be less than 3 MB."); e.target.value = ""; return; }
    const reader = new FileReader();
    reader.onloadend = () => this.setState({ bannerImagePreview: reader.result, bannerFile: file });
    reader.readAsDataURL(file);
  };

  saveBanner = async () => {
    const { bannerTitle, bannerFile, editingBanner } = this.state;
    this.setState({ bannerSaving: true });
    try {
      const fd = new FormData();
      fd.append("page_Name", bannerTitle);
      if (bannerFile) fd.append("files", bannerFile);
      if (editingBanner) fd.append("id", editingBanner.Banner_id);
      const url = editingBanner ? `${API_URL}/api/banners/updateBanner` : `${API_URL}/api/banners/addBanner`;
      const res  = await fetch(url, { method: "POST", body: fd });
      const json = await res.json();
      this.setState({ bannerSaving: false });
      if (json.status) { this.showSuccess(editingBanner ? "Banner updated!" : "Banner saved!"); setTimeout(() => this.goToBannerList(), 1000); }
      else { this.showError(json.message || "Save failed."); }
    } catch { this.setState({ bannerSaving: false }); this.showError("Cannot connect to API."); }
  };

  deleteBanner = async (id) => {
    if (!window.confirm("Delete this banner?")) return;
    try {
      const res  = await fetch(`${API_URL}/api/banners/deleteBanner`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id }) });
      const json = await res.json();
      if (json.message === "Banner deleted successfully") { this.showSuccess("Banner deleted."); this.fetchBanners(); }
      else { this.showError(json.message || "Delete failed."); }
    } catch { this.showError("Cannot connect to API."); }
  };

  // ════════════════════
  //  SERVICES / BLOG
  // ════════════════════

  fetchServices = async () => {
    this.setState({ serviceLoading: true });
    try {
      const res  = await fetch(`${API_URL}/api/blog/getblogs`, { method: "GET", headers: { "ngrok-skip-browser-warning": "true" } });
      const json = await res.json();
      if (json.status) {
        this.setState({ serviceList: json.data || [], serviceLoading: false });
      } else { this.setState({ serviceLoading: false }); }
    } catch { this.setState({ serviceLoading: false }); this.showError("Cannot connect to API."); }
  };

  openServiceForm = (item = null) => {
    this.setState({
      serviceView    : "form",
      editingService : item,
      serviceForm    : item
        ? {
            name          : item.blog_Name   || "",
            description   : item.description || "",
            event         : item.event_type    || "",   // ← reads event_type from API
            url           : item.page_url    || "",
            images        : [],
            bannerPreview : null,
            bannerFile    : null,
            saving        : false,
          }
        : emptyServiceForm(),
    });
  };

  goToServiceList = () => { this.setState({ serviceView: "list", editingService: null }); this.fetchServices(); };

  updateForm = (field, value) => {
    this.setState((prev) => ({ serviceForm: { ...prev.serviceForm, [field]: value } }));
  };

  handleNameChange = (e) => {
    const name = e.target.value;
    const slug = "/blog/" + name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");
    this.setState((prev) => ({ serviceForm: { ...prev.serviceForm, name, url: slug } }));
  };

  handleGalleryImages = (e) => {
    const files = Array.from(e.target.files);
    const oversized = files.find(f => f.size > MAX_IMAGE_SIZE);
    if (oversized) { this.showError("Each image must be less than 3 MB."); e.target.value = ""; return; }
    let loaded = 0; const newImages = [];
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        newImages.push({ file, preview: reader.result }); loaded++;
        if (loaded === files.length) {
          this.setState((prev) => ({ serviceForm: { ...prev.serviceForm, images: [...prev.serviceForm.images, ...newImages] } }));
        }
      };
      reader.readAsDataURL(file);
    });
  };

  removeGalleryImage = (i) => {
    this.setState((prev) => ({ serviceForm: { ...prev.serviceForm, images: prev.serviceForm.images.filter((_, idx) => idx !== i) } }));
  };

  handleServiceBanner = (e) => {
    const file = e.target.files[0]; if (!file) return;
    if (file.size > MAX_IMAGE_SIZE) { this.showError("Image size must be less than 3 MB."); e.target.value = ""; return; }
    const reader = new FileReader();
    reader.onloadend = () => this.setState((prev) => ({ serviceForm: { ...prev.serviceForm, bannerPreview: reader.result, bannerFile: file } }));
    reader.readAsDataURL(file);
  };

  saveService = async () => {
    const { serviceForm, editingService } = this.state;
    if (!serviceForm.name.trim()) return this.showError("Blog name is required.");
    if (!serviceForm.event.trim()) return this.showError("Blog event is required.");
    if (!serviceForm.description.trim()) return this.showError("Blog description is required.");
    if (!editingService && !serviceForm.bannerFile) {
      return this.showError("Banner image is required.");
    }
    if (editingService && !serviceForm.bannerFile && !editingService.BannerUrl) {
      return this.showError("Banner image is required.");
    }
    this.setState((prev) => ({ serviceForm: { ...prev.serviceForm, saving: true } }));
    try {
      const fd = new FormData();
      fd.append("blog_Name",   serviceForm.name);
      fd.append("description", serviceForm.description);
      fd.append("event",        serviceForm.event);         // ← backend SP expects 'event'
      fd.append("page_url",    serviceForm.url);
      fd.append("is_active",   1);
      fd.append("sort_order",  0);
      serviceForm.images.forEach((img) => fd.append("image", img.file));
      if (serviceForm.bannerFile) fd.append("banner", serviceForm.bannerFile);
      if (editingService) fd.append("blog_id", editingService.blog_id);
      const url = editingService ? `${API_URL}/api/blog/updateBlog` : `${API_URL}/api/blog/addBlog`;
      const res  = await fetch(url, { method: "POST", body: fd });
      const json = await res.json();
      this.setState((prev) => ({ serviceForm: { ...prev.serviceForm, saving: false } }));
      if (json.status) { this.showSuccess(editingService ? "Blog updated!" : "Blog added!"); setTimeout(() => this.goToServiceList(), 1000); }
      else { this.showError(json.message || "Save failed."); }
    } catch { this.setState((prev) => ({ serviceForm: { ...prev.serviceForm, saving: false } })); this.showError("Cannot connect to API."); }
  };

  deleteService = async (iddata) => {
    if (!window.confirm("Delete this blog?")) return;
    try {
      const res  = await fetch(`${API_URL}/api/blog/deleteBlog`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id: iddata }) });
      const json = await res.json();
      if (json.status) { this.showSuccess("Blog deleted."); this.fetchServices(); }
      else { this.showError(json.message || "Delete failed."); }
    } catch { this.showError("Cannot connect to API."); }
  };

    deleteGalleryImage = async (imageId) => {
    if (!window.confirm("Delete this image permanently?")) return;
    try {
      const res = await fetch(`${API_URL}/api/gallery/deleteGalleryById`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: imageId }),
      });
      const json = await res.json();
      if (json.status) {
        this.showSuccess("Image deleted.");
        // Remove from editingService.imageArray locally — no refetch
        this.setState((prev) => ({
          editingService: {
            ...prev.editingService,
            imageArray: (prev.editingService.imageArray || []).filter(
              (img) => img.image_id !== imageId
            ),
          },
        }));
      } else {
        this.showError(json.message || "Delete failed.");
      }
    } catch {
      this.showError("Cannot connect to API.");
    }
  };

  // ════════════════════
  //  RENDER
  // ════════════════════
  render() {
    const {
      activeTab,
      bannerView, bannerList, bannerLoading, bannerTitle, bannerImagePreview, bannerSaving, editingBanner,
      serviceView, serviceList, serviceLoading, serviceForm, editingService,
      successMessage, errorMessage,
    } = this.state;

    return (
      <div>
        <div className="page-header">
          <h2>Blog Page Manager</h2>
          <p>Manage banner and service details for the Blog page.</p>
        </div>

        {successMessage && <div style={s.success}>{successMessage}</div>}
        {errorMessage   && <div style={s.error}>{errorMessage}</div>}

        <div className="rsp-tab-row" style={s.tabRow}>
          {["banner", "services"].map((tab) => (
            <button key={tab} style={activeTab === tab ? s.tabActive : s.tab} onClick={() => this.setState({ activeTab: tab })}>
              {tab === "blog"}
            </button>
          ))}
        </div>

        {/* ── BLOG TAB ── */}
        {activeTab === "blog" && (
          <div>
            {serviceView === "list" && (
              <div>
                <div className="rsp-list-top" style={s.listTopBar}>
                  <span style={s.countText}>{serviceLoading ? "Loading..." : `${serviceList.length} blog${serviceList.length !== 1 ? "s" : ""} found`}</span>
                  <button className="rsp-btn-save" style={s.btnSave} onClick={() => this.openServiceForm()}>+ Add Blog</button>
                </div>
                {serviceLoading && <div style={s.emptyBox}><p style={s.emptyText}>Loading Blog...</p></div>}
                {!serviceLoading && serviceList.length === 0 && (
                  <div style={s.emptyBox}>
                    <p style={{ ...s.emptyText, marginBottom: "12px" }}>No Blog found.</p>
                    <button className="rsp-btn-save" style={s.btnSave} onClick={() => this.openServiceForm()}>+ Add First Blog</button>
                  </div>
                )}

                {!serviceLoading && serviceList.map((item, i) => {
                  const thumb = item.BannerUrl || (item.images && item.images.split(",")[0]);
                  return (
                    <div key={item.blog_id || i} className="rsp-list-card" style={s.listCard}>
                      {thumb ? (
                        <img src={imgUrl(thumb.trim())} alt={item.blog_Name} className="rsp-thumb-wide" style={s.thumbWide} />
                      ) : (
                        <div style={s.thumbPlaceholder}>No Image</div>
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={s.cardTitle}>
                          {item.blog_Name || "—"}
                          <span style={s.idBadge}>ID: {item.blog_id}</span>
                        </div>
                        {item.event_type && (
                          <p style={{ fontSize: "11px", color: "#e8a020", marginTop: "2px", fontWeight: "600" }}>
                            🎫 {item.event_type}
                          </p>
                        )}
                        {item.description && (
                          <p style={s.metaText}>{stripHtml(item.description).substring(0, 120)}</p>
                        )}
                        {item.page_url && (
                          <p style={{ fontSize: "11px", color: "#aaa", marginTop: "2px" }}>{item.page_url}</p>
                        )}
                      </div>
                      <div className="rsp-action-btns" style={s.actionBtns}>
                        <button className="rsp-btn-edit" style={s.btnEdit}   onClick={() => this.openServiceForm(item)}>Edit</button>
                        <button className="rsp-btn-delete" style={s.btnDelete} onClick={() => this.deleteService(item.blog_id)}>Delete</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {serviceView === "form" && (
              <div>
                <div className="rsp-form-top" style={s.formTopBar}>
                  <button className="rsp-btn-back" style={s.btnBack} onClick={this.goToServiceList}>Back to List</button>
                  <h3 style={s.formTitle}>
                    {editingService ? `Edit — ${editingService.blog_Name}` : "Add New Blog"}
                  </h3>
                </div>

                <div className="card">

                  {/* Row 1: Blog Name + URL Slug */}
                  <div className="rsp-two-col" style={s.twoCol}>
                    <div className="rsp-form-group" style={s.formGroup}>
                      <label style={s.label}>Blog Name *</label>
                      <input
                        className="rsp-input" style={s.input}
                        type="text"
                        value={serviceForm.name}
                        placeholder="e.g. Sports Events"
                        onChange={this.handleNameChange}
                      />
                    </div>
                    <div className="rsp-form-group" style={s.formGroup}>
                      <label style={s.label}>URL Slug <span style={{ color: "#aaa", fontWeight: "400", marginLeft: "6px" }}>(auto-generated)</span></label>
                      <input
                        style={{ ...s.input, background: "#f5f5f5", color: "#888", cursor: "not-allowed" }}
                        type="text"
                        value={serviceForm.url}
                        placeholder="/blog/sports-events"
                        readOnly
                        disabled
                      />
                    </div>
                  </div>

                  {/* Row 2: Event (NEW FIELD) */}
                  <div className="rsp-form-group" style={s.formGroup}>
                    <label style={s.label}>Event *</label>
                    <input
                      className="rsp-input" style={s.input}
                      type="text"
                      value={serviceForm.event}
                      placeholder="e.g. Annual Sports Gala 2025"
                      onChange={(e) => this.updateForm("event", e.target.value)}
                    />
                  </div>

                  {/* Row 3: Description */}
                  <div className="rsp-form-group" style={s.formGroup}>
                    <label style={s.label}>Description *</label>
                    <RichTextEditor
                      key={`desc-${editingService?.blog_id || "new"}`}
                      value={serviceForm.description}
                      onChange={(val) => this.updateForm("description", val)}
                    />
                  </div>

                  {/* Banner Image */}
                  <div className="rsp-form-group" style={s.formGroup}>
                    <label style={s.label}>Banner Image * <span style={{ color: "#aaa", fontWeight: "400" }}>(single — card background)</span></label>
                    {editingService && !serviceForm.bannerPreview && editingService.BannerUrl && (
                      <div style={{ marginBottom: "10px" }}>
                        <p style={s.hint}>Current banner:</p>
                        <img src={imgUrl(editingService.BannerUrl)} alt="current" style={s.currentImg} />
                      </div>
                    )}
                    {serviceForm.bannerPreview ? (
                      <div className="rsp-preview-box" style={s.previewBox}>
                        <img src={serviceForm.bannerPreview} alt="preview" style={s.previewImg} />
                        <div style={s.overlay}>
                          <button className="rsp-btn-delete" style={s.btnDelete} onClick={() => this.setState((p) => ({ serviceForm: { ...p.serviceForm, bannerPreview: null, bannerFile: null } }))}>Remove</button>
                          <label className="rsp-btn-dark" style={s.btnDark}>Change<input type="file" accept="image/*" onChange={this.handleServiceBanner} style={{ display: "none" }} /></label>
                        </div>
                      </div>
                    ) : (
                      <label className="rsp-upload-box" style={s.uploadBox}>
                        <p style={s.uploadText}>{editingService ? "Click to replace banner" : "Click to upload banner image"}</p>
                        <p style={s.uploadHint}>JPG, PNG — card thumbnail on website</p>
                        <input type="file" accept="image/*" onChange={this.handleServiceBanner} style={{ display: "none" }} />
                      </label>
                    )}
                  </div>

                  {/* Gallery Images */}
                  <div className="rsp-form-group" style={s.formGroup}>
                    <label style={s.label}>Gallery Images <span style={{ color: "#aaa", fontWeight: "400" }}>(multiple)</span></label>
                    
                      {editingService &&
                      (() => {
                        const validImages = (
                          editingService.imageArray || []
                        ).filter((img) => img.image_id && img.image_url);

                        if (validImages.length === 0) {
                          return (
                            <div style={{ marginBottom: "10px" }}>
                              <p style={s.hint}>No images uploaded yet.</p>
                            </div>
                          );
                        }

                        return (
                          <div style={{ marginBottom: "10px" }}>
                            <p style={s.hint}>
                              Current gallery ({validImages.length} image(s)):
                            </p>
                            <div className="rsp-image-grid" style={s.imageGrid}>
                              {validImages.map((img) => (
                                <div
                                  key={img.image_id}
                                  style={{ position: "relative" }}
                                >
                                  <img
                                    src={imgUrl(img.image_url)}
                                    alt={`gallery-${img.image_id}`}
                                    style={s.imgThumb}
                                  />
                                  <button
                                    type="button"
                                    title="Delete image permanently"
                                    style={s.deleteImgBtn}
                                    onClick={() =>
                                      this.deleteGalleryImage(img.image_id)
                                    }
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}

                    {serviceForm.images.length > 0 && (
                      <div style={{ marginBottom: "10px" }}>
                        <p style={s.hint}>{serviceForm.images.length} new image(s) selected:</p>
                        <div className="rsp-image-grid" style={s.imageGrid}>
                          {serviceForm.images.map((img, i) => (
                            <div key={i} style={{ position: "relative" }}>
                              <img src={img.preview} alt="" style={s.imgThumb} />
                              <button style={s.removeImgBtn} onClick={() => this.removeGalleryImage(i)}>X</button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <label className="rsp-upload-box" style={s.uploadBox}>
                      <p style={s.uploadText}>{serviceForm.images.length > 0 ? "Click to add more images" : "Click to select gallery images"}</p>
                      <p style={s.uploadHint}>Use Ctrl+Click to select multiple files</p>
                      <input type="file" accept="image/*" multiple onChange={this.handleGalleryImages} style={{ display: "none" }} />
                    </label>
                  </div>

                  <div className="rsp-form-actions" style={s.formActions}>
                    <button className="rsp-btn-back" style={s.btnBack} onClick={this.goToServiceList}>Cancel</button>
                    <button className="rsp-btn-save" style={s.btnSave} onClick={this.saveService} disabled={serviceForm.saving}>
                      {serviceForm.saving ? "Saving..." : editingService ? `Update ${serviceForm.name || "Blog"}` : `Save ${serviceForm.name || "Blog"}`}
                    </button>
                  </div>

                </div>
              </div>
            )}
          </div>
        )}

      </div>
    );
  }
}

const s = {
  success         : { background: "#e8f5e9", color: "#2e7d32", border: "1px solid #c8e6c9", borderRadius: "8px", padding: "12px 16px", marginBottom: "16px", fontSize: "14px" },
  error           : { background: "#ffebee", color: "#c62828", border: "1px solid #ffcdd2", borderRadius: "8px", padding: "12px 16px", marginBottom: "16px", fontSize: "14px" },
  tabRow          : { display: "flex", gap: "10px", borderBottom: "2px solid #e8e8e8", marginBottom: "20px" },
  tab             : { padding: "10px 20px", background: "none", border: "none", borderBottom: "3px solid transparent", fontSize: "14px", color: "#888", cursor: "pointer", marginBottom: "-2px", fontWeight: "500" },
  tabActive       : { padding: "10px 20px", background: "none", border: "none", borderBottom: "3px solid #e8a020", fontSize: "14px", color: "#e8a020", cursor: "pointer", marginBottom: "-2px", fontWeight: "600" },
  listTopBar      : { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" },
  countText       : { fontSize: "14px", color: "#888" },
  listCard        : { background: "white", borderRadius: "10px", padding: "16px 18px", border: "1px solid #e8e8e8", marginBottom: "12px", display: "flex", alignItems: "center", gap: "16px" },
  cardTitle       : { fontSize: "15px", color: "#1a1a2e", fontWeight: "600", display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px", flexWrap: "wrap" },
  idBadge         : { background: "#f0f2f5", color: "#888", border: "1px solid #e0e0e0", borderRadius: "10px", padding: "2px 8px", fontSize: "11px", fontWeight: "400" },
  metaText        : { fontSize: "12px", color: "#888", marginTop: "2px" },
  emptyBox        : { background: "white", borderRadius: "10px", padding: "48px 20px", border: "1px solid #e8e8e8", textAlign: "center" },
  emptyText       : { color: "#aaa", fontSize: "14px" },
  thumbWide       : { width: "120px", height: "70px", objectFit: "cover", borderRadius: "6px", border: "1px solid #e8e8e8", flexShrink: 0 },
  thumbPlaceholder: { width: "120px", height: "70px", background: "#f0f2f5", borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", color: "#aaa", flexShrink: 0 },
  actionBtns      : { display: "flex", flexDirection: "column", gap: "6px", flexShrink: 0 },
  btnEdit         : { background: "#e3f2fd", color: "#1565c0", border: "1px solid #bbdefb", padding: "6px 14px", borderRadius: "6px", fontSize: "12px", cursor: "pointer", fontWeight: "600", minWidth: "64px" },
  btnDelete       : { background: "#ffebee", color: "#c62828", border: "1px solid #ffcdd2", padding: "6px 14px", borderRadius: "6px", fontSize: "12px", cursor: "pointer", fontWeight: "600", minWidth: "64px" },
  formTopBar      : { display: "flex", alignItems: "center", gap: "14px", marginBottom: "16px" },
  formTitle       : { fontSize: "16px", color: "#1a1a2e", margin: 0 },
  formActions     : { display: "flex", justifyContent: "flex-end", gap: "10px", borderTop: "1px solid #f0f0f0", paddingTop: "16px", marginTop: "8px" },
  formGroup       : { marginBottom: "20px" },
  label           : { display: "block", fontSize: "13px", color: "#555", fontWeight: "600", marginBottom: "6px" },
  input           : { width: "100%", padding: "10px 14px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px", outline: "none", fontFamily: "Arial, sans-serif", boxSizing: "border-box" },
  twoCol          : { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" },
  uploadBox       : { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", border: "2px dashed #ddd", borderRadius: "10px", padding: "28px 20px", cursor: "pointer", background: "#fafafa", textAlign: "center" },
  uploadText      : { fontSize: "14px", color: "#555", marginBottom: "4px" },
  uploadHint      : { fontSize: "11px", color: "#aaa" },
  previewBox      : { position: "relative", borderRadius: "10px", overflow: "hidden", border: "1px solid #e8e8e8" },
  previewImg      : { width: "100%", height: "220px", objectFit: "cover", display: "block" },
  currentImg      : { width: "200px", height: "90px", objectFit: "cover", borderRadius: "6px", border: "1px solid #e8e8e8" },
  overlay         : { position: "absolute", bottom: "10px", right: "10px", display: "flex", gap: "8px" },
  imageGrid       : { display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "8px", marginBottom: "10px" },
  imgThumb        : { width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: "6px", border: "1px solid #e8e8e8" },
  removeImgBtn    : { position: "absolute", top: "2px", right: "2px", width: "18px", height: "18px", background: "#e53935", color: "#fff", border: "none", borderRadius: "50%", fontSize: "10px", cursor: "pointer", fontWeight: "700" },
  hint            : { fontSize: "12px", color: "#888", marginBottom: "6px" },
  btnDark         : { background: "#1a1a2e", color: "#fff", border: "none", padding: "7px 14px", borderRadius: "6px", fontSize: "12px", cursor: "pointer", fontWeight: "600" },
  btnSave         : { background: "#e8a020", color: "#fff", border: "none", padding: "10px 20px", borderRadius: "8px", fontSize: "14px", fontWeight: "600", cursor: "pointer" },
  btnBack         : { background: "#f0f2f5", color: "#333", border: "1px solid #ddd", padding: "9px 16px", borderRadius: "8px", fontSize: "13px", cursor: "pointer", fontWeight: "500" },
  deleteImgBtn    : { position: "absolute", top: "4px", right: "4px", width: "24px", height: "24px", background: "#e53935", color: "#fff", border: "2px solid #fff", borderRadius: "50%", fontSize: "16px", cursor: "pointer", fontWeight: "700", lineHeight: "1", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 4px rgba(0,0,0,0.25)", padding: 0 },
};

export default Blog;